import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, ChatCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Sprint Feb 2026 — Anti-pollution console : passé de 30s à 2min.
// La présence n'est pas critique, ces appels génèrent des erreurs réseau
// visibles dans la console quand la connexion Internet vacille.
const HEARTBEAT_MS = 120000;      // ping toutes les 2 minutes
const POLL_MS = 120000;           // refresh online list toutes les 2 minutes

/**
 * Indicateur "Agents en ligne" - tenant-scoped.
 * - Envoie un heartbeat toutes les 30s tant que l'onglet est visible
 * - Recharge la liste online toutes les 30s
 * - Affiche compteur avec popover au clic (liste des noms)
 */
const PresenceIndicator = ({ unreadChat = 0 }) => {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const heartbeatRef = useRef(null);
  const pollRef = useRef(null);
  const popoverRef = useRef(null);
  const navigate = useNavigate();

  const sendHeartbeat = async () => {
    try {
      await axios.post(`${API}/presence/heartbeat`, {}, { withCredentials: true });
    } catch (e) {
      // silencieux : pas de toast pour ne pas spammer
    }
  };

  const fetchOnline = async () => {
    try {
      const r = await axios.get(`${API}/presence/online`, { withCredentials: true });
      setCount(r.data.count || 0);
      setItems(r.data.items || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      await sendHeartbeat();
      await fetchOnline();
    };
    // Premier tick immediate
    tick();
    heartbeatRef.current = setInterval(tick, HEARTBEAT_MS);
    pollRef.current = setInterval(fetchOnline, POLL_MS);
    // Refresh quand l'onglet redevient visible
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(heartbeatRef.current);
      clearInterval(pollRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Click outside pour fermer le popover
  useEffect(() => {
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const roleLabel = (role) => {
    if (role === 'chef_agence') return 'Chef';
    if (role === 'agent') return 'Agent';
    return role || '';
  };

  return (
    <div className="relative" ref={popoverRef} data-testid="presence-indicator">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center gap-2 px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-sm font-medium text-emerald-800 transition-colors"
        title={`${count} agent${count > 1 ? 's' : ''} en ligne${unreadChat > 0 ? ` · ${unreadChat} message(s) non lu(s)` : ''}`}
        data-testid="presence-toggle-btn"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
        </span>
        <Users size={16} weight="bold" />
        <span data-testid="presence-count">{count}</span>
        <span className="hidden md:inline">en ligne</span>
        {unreadChat > 0 && (
          <span
            data-testid="presence-unread-dot"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white animate-pulse"
            title={`${unreadChat} message(s) non lu(s)`}
          >
            {unreadChat > 9 ? '9+' : unreadChat}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 rounded-xl border border-stone-200 bg-white shadow-xl z-50 overflow-hidden"
          data-testid="presence-popover"
        >
          <div className="px-4 py-3 border-b border-stone-100 bg-stone-50">
            <p className="text-xs font-semibold tracking-wide text-stone-500 uppercase">Agents en ligne</p>
            <p className="text-sm text-stone-800 mt-0.5">
              {count === 0 ? 'Aucun agent connecté' : `${count} agent${count > 1 ? 's' : ''} actif${count > 1 ? 's' : ''}`}
            </p>
          </div>
          <ul className="max-h-72 overflow-y-auto divide-y divide-stone-100">
            {items.length === 0 ? (
              <li className="px-4 py-3 text-sm text-stone-500 italic">Personne en ligne actuellement.</li>
            ) : (
              items.map((u) => (
                <li
                  key={u.id}
                  className="px-4 py-2.5 flex items-center gap-3 hover:bg-stone-50"
                  data-testid={`presence-user-${u.id}`}
                >
                  <div className="relative">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-sky-500 text-white font-bold flex items-center justify-center text-sm">
                      {(u.nom || '?').charAt(0).toUpperCase()}
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 block h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white"></span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">
                      {u.nom || 'Anonyme'}
                      {u.is_self && <span className="ml-1 text-xs text-stone-400">(vous)</span>}
                    </p>
                    <p className="text-xs text-stone-500 truncate">
                      {roleLabel(u.role)}{u.poste ? ` · ${u.poste}` : ''}
                    </p>
                  </div>
                </li>
              ))
            )}
          </ul>
          {/* Footer : Accès direct au chat équipe (remplace l'entrée sidebar) */}
          <div className="px-3 py-2 border-t border-stone-100 bg-stone-50">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/chat'); }}
              data-testid="presence-open-chat"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-br from-sky-600 to-indigo-600 text-white text-sm font-semibold hover:from-sky-700 hover:to-indigo-700 transition-all shadow-sm"
            >
              <ChatCircle size={14} weight="bold" />
              Ouvrir le Chat équipe
              {unreadChat > 0 && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unreadChat > 9 ? '9+' : unreadChat}
                </span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresenceIndicator;
